import React from "react";
import styled from "styled-components";
import { useNavigate, Link } from "react-router-dom";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaFacebook,
  FaInstagram,
  FaWhatsapp,
} from "react-icons/fa";

// ========================= STYLED COMPONENTS ========================= //

const FooterWrapper = styled.footer`
  background: #0d0d0d;
  color: #d4d4d4;
  padding: 60px 0 20px;
  width: 100%;
`;

const Container = styled.div`
  max-width: 1300px;
  margin: auto;
  padding: 0 30px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 60px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: 48px;
    padding: 0 20px;
  }
`;


const FooterColumn = styled.div``;

const Title = styled.h2`
  color: #fff;
  font-size: 20px;
  margin-bottom: 18px;
  font-weight: 600;
  position: relative;
  display: inline-block;

  @media (max-width: 820px) {
    font-size: 18px;
    margin-bottom: 14px;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -6px;
    left: 0;
    width: 40px;
    height: 3px;
    background: ${(props) => props.theme.colors.secondary};
    border-radius: 2px;

    @media (max-width: 820px) {
      left: 50%;
      transform: translateX(-50%);
      width: 50px;
    }
  }
`;


const AboutText = styled.p`
  font-size: 14px;
  line-height: 1.7;
  color: #cfcfcf;

  @media (max-width: 820px) {
    font-size: 13.5px;
    line-height: 1.8;
  }
`;


const LinksList = styled.ul`
  list-style: none;
  padding: 0;
`;

const NavLink = styled.li`
  margin-bottom: 12px;
  color: #cfcfcf;
  font-size: 14px;
  transition: 0.3s ease;
  cursor: pointer;   /* ✅ THIS IS THE KEY */

  &:hover {
    color: #ffffff;
  }

  @media (max-width: 820px) {
    margin-bottom: 16px;
    padding: 8px 0;
    font-size: 15px;
  }
`;



const ContactRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;

  @media (max-width: 820px) {
    justify-content: flex-start; /* ✅ start from left */
    text-align: left;            /* ✅ left-aligned text */
    flex-direction: row;         /* ✅ keep icon + text same line */
    gap: 10px;
  }

  a,
  span {
    font-size: 14px;
    color: #cfcfcf;
    line-height: 1.5;
  }
`;




const Icon = styled.div`
  font-size: 16px;
  color: ${(props) => props.theme.colors.secondary};
  flex-shrink: 0; /* prevents icon from shrinking */

  @media (max-width: 820px) {
    font-size: 18px;
  }
`;


const SocialIcons = styled.div`
  display: flex;
  gap: 10px; /* ✅ reduced gap */
  margin-top: 18px;
  justify-content: flex-start;

  @media (max-width: 820px) {
    justify-content: center;
  }

  a {
    color: #cfcfcf;
    font-size: 20px;
    padding: 8px; /* ✅ reduced padding */
    transition: 0.3s ease;

    &:hover {
      color: ${(props) => props.theme.colors.secondary};
      transform: translateY(-3px);
    }
  }
`;



const Copyright = styled.div`
  text-align: center;
  margin-top: 40px;
  padding-top: 18px;
  font-size: 13px;
  border-top: 1px solid #2a2a2a;
  color: #9c9c9c;

  @media (max-width: 820px) {
    font-size: 12px;
    margin-top: 32px;
  }
`;
const DeveloperCredit = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #8a8a8a;

  a {
    color: ${(props) => props.theme.colors.secondary};
    text-decoration: none;
    font-weight: 500;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 820px) {
    font-size: 11.5px;
  }
`;
const FooterLogo = styled.img`
  margin-top: 18px;
  max-width: 160px;
  width: 100%;
  height: auto;

  // filter: brightness(0) invert(1); /* ✅ makes logo white-friendly on dark bg */

  @media (max-width: 820px) {
    margin-top: 20px;
    max-width: 140px;
  }
`;



// ========================= FOOTER COMPONENT ========================= //

export default function Footer() {
  const navigate = useNavigate();  // ✅ Hook at top-level (correct)

  const handleNavClick = (section) => {
    if (window.location.pathname === "/") {
      // Already on homepage → scroll to section
      const sectionElement = document.getElementById(section);
      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // Navigate to homepage with section hash
      navigate(`/#${section}`);
    }
  };

  return (
    <FooterWrapper>
      <Container>

        {/* ABOUT SECTION */}
        <FooterColumn>
          <Title>Quick Overview</Title>
          <AboutText>
            LK Education is a leading institute in Tirunelveli specializing in
            accounting and Tally training. We provide industry-oriented
            programs such as TallyPrime, GST, TDS, Payroll, MS Office, and
            Accounting Basics with real-time practical training and
            professional certification.
          </AboutText>
           {/* LOGO */}
  <FooterLogo
    src="https://cdn.shopify.com/s/files/1/0851/4607/5431/files/Untitled_design_4_abf43933-4ac4-44bf-a933-92e15b3e8c13.png?v=1767814970"
    alt="LK Education Logo"
  />
        </FooterColumn>

        {/* QUICK LINKS */}
        <FooterColumn>
          <Title>Quick Links</Title>
          <LinksList>
            <NavLink onClick={() => navigate("/")}>Home</NavLink>
            <NavLink onClick={() => navigate("/about")}>About</NavLink>
            <NavLink onClick={() => handleNavClick("contact")}>Contact</NavLink>
            <NavLink onClick={() => handleNavClick("services")}>Our Courses</NavLink>      
          <NavLink onClick={() => navigate("/certifications")}>Certifications</NavLink>
          <NavLink onClick={() => navigate("/privacypolicy")}>Privacy & Policy</NavLink>
          </LinksList>
        </FooterColumn>

        {/* CONTACT SECTION */}
        <FooterColumn>
          <Title>Contact Us</Title>

          <ContactRow>
            <Icon><FaPhoneAlt /></Icon>
            <a href="tel:+919876543210">+91 98765 43210</a>
          </ContactRow>

          <ContactRow>
            <Icon><FaEnvelope /></Icon>
            <a href="mailto:lkeduganesh@gmail.com">lkeduganesh@gmail.com</a>
          </ContactRow>

          <ContactRow>
            <Icon><FaMapMarkerAlt /></Icon>
            <span>
              182, S. N. High Road, Sripuram, Tirunelveli Junction – 627001
            </span>
          </ContactRow>


          <SocialIcons>

            <a
              href="https://www.instagram.com/lk_edu_com?igsh=OGJndWtzb20zaHZ0"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaInstagram />
            </a>
            <a href="https://wa.me/+919876543210"><FaWhatsapp /></a>
          </SocialIcons>
        </FooterColumn>

      </Container>

     <Copyright>
  © {new Date().getFullYear()} LK Education. All Rights Reserved.

  <DeveloperCredit>
    Design and Developed by{" "}
    <a
      href="https://pragantechsolutions.com/"
      target="_blank"
      rel="noopener noreferrer"
    >
      Pragan Tech Solutions
    </a>
  </DeveloperCredit>
</Copyright>

    </FooterWrapper>
  );
}
